<template>
  <div class="client-package-upload">
    <input
      ref="fileInput"
      type="file"
      class="hidden-input"
      :accept="accept"
      @change="onInputChange"
    />
    <div
      v-if="!selectedFile"
      class="upload-dropzone"
      :class="{ 'is-dragover': dragOver, 'is-disabled': disabled || uploading }"
      @click="openPicker"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop"
    >
      <div class="dropzone-inner">
        <i class="el-icon-upload dropzone-icon"></i>
        <p class="dropzone-title">{{ $tt("点击或拖拽文件到此处上传") }}</p>
        <p v-if="tip" class="dropzone-tip">{{ tip }}</p>
        <p class="dropzone-hint">{{ $tt("支持断点续传，大文件可放心上传") }}</p>
      </div>
    </div>

    <div v-else class="upload-file-card">
      <div class="file-main">
        <div class="file-icon-wrap">
          <i class="el-icon-document file-icon"></i>
        </div>
        <div class="file-meta">
          <div class="file-name" :title="selectedFile.name">{{ selectedFile.name }}</div>
          <div class="file-size">{{ formatSize(selectedFile.size) }}</div>
        </div>
        <el-button
          v-if="!uploading"
          type="text"
          class="remove-btn"
          icon="el-icon-delete"
          @click="removeFile"
        >{{ $tt("移除") }}</el-button>
      </div>
      <div v-if="uploading || progress > 0" class="progress-wrap">
        <el-progress
          :percentage="progress"
          :status="progressStatus"
          :stroke-width="10"
        ></el-progress>
        <span class="progress-text">{{ progress }}%</span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  uploadClientPackage,
  removeClientPackageUpload,
  formatFileSize,
} from "@/utils/clientPackageChunkUpload";

export default {
  name: "ClientPackageUpload",
  props: {
    packageType: {
      type: String,
      required: true,
    },
    /** qt 上传时传 channel：qt_win / qt_kylin / qt_mac / qt_ios，决定 mac/ios/qt 磁盘目录 */
    packageChannel: {
      type: String,
      default: "",
    },
    accept: {
      type: String,
      default: "",
    },
    tip: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    paramKey: {
      type: String,
      required: true,
    },
    beforeSelect: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      selectedFile: null,
      uploadId: null,
      dragOver: false,
      uploading: false,
      progress: 0,
      progressStatus: null,
    };
  },
  methods: {
    formatSize(bytes) {
      return formatFileSize(bytes);
    },
    openPicker() {
      if (this.disabled || this.uploading) return;
      this.$refs.fileInput.click();
    },
    onDragOver() {
      if (this.disabled || this.uploading) return;
      this.dragOver = true;
    },
    async applyFile(file) {
      if (!file) return;
      if (this.beforeSelect) {
        const ok = this.beforeSelect(file);
        if (ok === false) return;
      }
      if (this.accept) {
        const acceptList = this.accept.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
        const name = (file.name || "").toLowerCase();
        const matched = acceptList.some((ext) => {
          if (ext.startsWith(".")) {
            return name.endsWith(ext);
          }
          return true;
        });
        if (!matched) {
          this.$message.error(this.$tt("文件格式不符合要求"));
          return;
        }
      }
      this.selectedFile = file;
      this.progress = 0;
      this.progressStatus = null;
      this.uploadId = null;
      this.$emit("change", file);
    },
    onInputChange(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      this.applyFile(file);
    },
    onDrop(e) {
      this.dragOver = false;
      if (this.disabled || this.uploading) return;
      const file = e.dataTransfer.files && e.dataTransfer.files[0];
      this.applyFile(file);
    },
    async removeFile() {
      if (this.uploading) return;
      await removeClientPackageUpload(
        this.packageType,
        this.selectedFile,
        this.uploadId,
        this.packageChannel
      );
      this.selectedFile = null;
      this.uploadId = null;
      this.progress = 0;
      this.progressStatus = null;
      this.$emit("change", null);
      this.$emit("remove");
    },
    getFile() {
      return this.selectedFile;
    },
    setProgress(percent) {
      this.progress = Math.min(100, Math.max(0, percent || 0));
    },
    async upload(formData) {
      if (!this.selectedFile) {
        throw new Error(this.$tt("请选择文件"));
      }
      this.uploading = true;
      this.progress = 0;
      this.progressStatus = null;
      try {
        const ret = await uploadClientPackage({
          packageType: this.packageType,
          packageChannel: this.packageChannel,
          file: this.selectedFile,
          paramKey: this.paramKey,
          formData,
          uploadId: this.uploadId,
          onProgress: (p) => {
            this.setProgress(p);
          },
        });
        this.setProgress(100);
        this.progressStatus = "success";
        return ret;
      } catch (err) {
        this.progressStatus = "exception";
        throw err;
      } finally {
        this.uploading = false;
      }
    },
    clear() {
      this.selectedFile = null;
      this.uploadId = null;
      this.progress = 0;
      this.progressStatus = null;
      this.uploading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.client-package-upload {
  width: 100%;
}

.hidden-input {
  display: none;
}

.upload-dropzone {
  border: 1px dashed #c0c4cc;
  border-radius: 8px;
  background: linear-gradient(180deg, #fafcff 0%, #f5f7fa 100%);
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;

  &:hover:not(.is-disabled) {
    border-color: #409eff;
    box-shadow: 0 2px 12px rgba(64, 158, 255, 0.12);
  }

  &.is-dragover {
    border-color: #409eff;
    background: #ecf5ff;
  }

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
}

.dropzone-inner {
  padding: 28px 16px;
  text-align: center;
}

.dropzone-icon {
  font-size: 42px;
  color: #409eff;
  margin-bottom: 8px;
}

.dropzone-title {
  margin: 0 0 6px;
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.dropzone-tip,
.dropzone-hint {
  margin: 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}

.upload-file-card {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.file-main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #ecf5ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.file-icon {
  font-size: 22px;
  color: #409eff;
}

.file-meta {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 14px;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.remove-btn {
  color: #f56c6c;
  flex-shrink: 0;
}

.progress-wrap {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 10px;

  .el-progress {
    flex: 1;
  }
}

.progress-text {
  font-size: 12px;
  color: #606266;
  min-width: 36px;
  text-align: right;
}
</style>
